
import React from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { X } from 'lucide-react';
import { FilterState } from '@/hooks/useTeacherProfilesFilters';

interface TeacherProfilesFiltersProps {
  filters: FilterState;
  filterOptions: {
    subjects: string[];
    gradeLevels: string[];
    courseTypes: string[];
    specialized: string[];
  };
  onFilterChange: (filterType: keyof FilterState, value: string) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

export const TeacherProfilesFilters: React.FC<TeacherProfilesFiltersProps> = ({
  filters,
  filterOptions,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
      <div className="space-y-2">
        <Label htmlFor="subject-filter">รายวิชา</Label>
        <Select 
          value={filters.subject} 
          onValueChange={(value) => onFilterChange('subject', value)}
        >
          <SelectTrigger id="subject-filter">
            <SelectValue placeholder="เลือกรายวิชา" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">ทั้งหมด</SelectItem>
            {filterOptions.subjects.map((subject) => (
              <SelectItem key={subject} value={subject}>
                {subject}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="grade-level-filter">ระดับชั้น</Label>
        <Select 
          value={filters.gradeLevel} 
          onValueChange={(value) => onFilterChange('gradeLevel', value)}
        >
          <SelectTrigger id="grade-level-filter">
            <SelectValue placeholder="เลือกระดับชั้น" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">ทั้งหมด</SelectItem>
            {filterOptions.gradeLevels.map((level) => (
              <SelectItem key={level} value={level}>
                {level}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="course-type-filter">ประเภทคอร์ส</Label>
        <Select 
          value={filters.courseType} 
          onValueChange={(value) => onFilterChange('courseType', value)}
        >
          <SelectTrigger id="course-type-filter">
            <SelectValue placeholder="เลือกประเภทคอร์ส" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">ทั้งหมด</SelectItem>
            {filterOptions.courseTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="specialized-filter">เฉพาะทาง</Label>
        <Select 
          value={filters.specialized} 
          onValueChange={(value) => onFilterChange('specialized', value)}
        >
          <SelectTrigger id="specialized-filter">
            <SelectValue placeholder="เลือกเฉพาะทาง" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">ทั้งหมด</SelectItem>
            {filterOptions.specialized.map((spec) => (
              <SelectItem key={spec} value={spec}>
                {spec}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>&nbsp;</Label>
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            ล้างตัวกรอง
          </Button>
        )}
      </div>
    </div>
  );
};
